import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Link } from 'react-router-dom';
import { Loader2, Gavel, CheckCircle, XCircle, Lightbulb } from 'lucide-react';

const pct = (v) => `${Math.round((v || 0) * 100)}%`;
const rateColor = (r) => (r >= 0.6 ? 'bg-green-500' : r >= 0.45 ? 'bg-yellow-500' : 'bg-red-500');

const WinRateByType = ({ data }) => (
  <div data-testid="judge-winrate-by-type" className="space-y-2">
    {Object.entries(data || {}).sort((a, b) => b[1].win_rate - a[1].win_rate).map(([type, s]) => (
      <div key={type} className="flex items-center gap-3">
        <span className="w-28 text-sm text-slate-300 capitalize">{type}</span>
        <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
          <div className={`h-full ${rateColor(s.win_rate)}`} style={{ width: pct(s.win_rate) }} />
        </div>
        <span className="w-12 text-right text-sm text-white font-semibold">{pct(s.win_rate)}</span>
        <span className="w-24 text-right text-xs text-slate-500">{s.rulings ? `${s.rulings} fallo(s)` : 'perfil'}</span>
      </div>
    ))}
  </div>
);

const RulingsHistory = ({ rulings }) => {
  if (!rulings?.length) {
    return <p className="text-sm text-slate-500">Aún no hay fallos registrados para este juez. Sube sentencias o espera la búsqueda automática nocturna.</p>;
  }
  return (
    <div data-testid="judge-rulings-history" className="space-y-2 max-h-64 overflow-y-auto pr-1">
      {rulings.map((r) => {
        const won = ['favor', 'voto_concurrente'].includes(r.judge_vote);
        return (
          <div key={r.id} className="bg-slate-800/60 rounded-lg p-3 border border-slate-700">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-white font-medium">{r.case_number}</p>
              <span className={`text-xs flex items-center gap-1 ${won ? 'text-green-400' : 'text-red-400'}`}>
                {won ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                {(r.judge_vote || '').replace('_', ' ')}
              </span>
            </div>
            <p className="text-xs text-slate-400">{r.ruling_date} · {r.case_type} · {r.outcome}</p>
            {r.ai_summary && <p className="text-xs text-slate-300 mt-1">{r.ai_summary}</p>}
          </div>
        );
      })}
    </div>
  );
};

export const JudgeProfileDialog = ({ judgeId, onClose }) => {
  const { api } = useAuth();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!judgeId) return;
    setProfile(null);
    api.get(`/judicial-intelligence/judges/${judgeId}/profile`).then((r) => setProfile(r.data));
  }, [judgeId, api]);

  const j = profile?.judge;
  return (
    <Dialog open={!!judgeId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="judge-profile-dialog" className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        {!profile ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-400" /></div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white"><Gavel className="w-5 h-5 text-amber-400" />{j.full_name || j.name}</DialogTitle>
              <DialogDescription className="text-slate-400">{j.position} · {j.court_name}{j.sala ? ` · ${j.sala}` : ''}</DialogDescription>
              <Link to={`/jueces/${j.id}`} data-testid="judge-full-page-link" className="text-xs text-amber-300 hover:underline">Ver ficha completa del juez →</Link>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-3 my-2">
              <div className="bg-slate-800 rounded-lg p-3"><p className="text-2xl font-bold">{profile.total_rulings}</p><p className="text-xs text-slate-400">Fallos registrados</p></div>
              <div className="bg-slate-800 rounded-lg p-3"><p className="text-2xl font-bold">{j.rulings_favor || 0}</p><p className="text-xs text-slate-400">Pro demandante</p></div>
              <div className="bg-slate-800 rounded-lg p-3"><p className="text-2xl font-bold">{j.dissenting_votes || 0}</p><p className="text-xs text-slate-400">Votos disidentes</p></div>
            </div>
            <h4 className="text-sm font-semibold text-slate-200 mt-2">Tasa de victoria del demandante por tipo de caso</h4>
            <WinRateByType data={profile.win_rate_by_type} />
            <h4 className="text-sm font-semibold text-slate-200 mt-4 flex items-center gap-2"><Lightbulb className="w-4 h-4 text-amber-400" />Qué funciona con este juez</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              {[...profile.recommended_strategies, ...profile.effective_arguments].map((s, i) => <li key={i}>• {s}</li>)}
              {profile.avoid_strategies.map((s, i) => <li key={`a${i}`} className="text-red-300">✕ {s}</li>)}
            </ul>
            <h4 className="text-sm font-semibold text-slate-200 mt-4">Historial de fallos</h4>
            <RulingsHistory rulings={profile.rulings} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
